/**
 * Debug Execution Prompt
 *
 * Helps users debug failed flow runs by analyzing error messages,
 * identifying root causes, and suggesting fixes.
 */

import type { Prompt, PromptMessage } from "@modelcontextprotocol/sdk/types.js";

// ============================================================================
// Prompt Definition
// ============================================================================

export const debugExecutionPrompt: Prompt = {
  name: "debug-execution",
  description:
    "Debug a failed Power Automate flow run. Analyzes errors, identifies root causes, and suggests fixes.",
  arguments: [
    {
      name: "flowId",
      description: "The flow ID to debug (use get_runs to find failed runs)",
      required: true,
    },
    {
      name: "runId",
      description: "Specific run ID to analyze (optional, uses most recent failed run if not provided)",
      required: false,
    },
    {
      name: "errorMessage",
      description: "The error message from the failed action (if known)",
      required: false,
    },
    {
      name: "failedAction",
      description: "The name of the action that failed (if known)",
      required: false,
    },
  ],
};

// ============================================================================
// Prompt Message Generation
// ============================================================================

export interface DebugExecutionArgs {
  flowId: string;
  runId?: string;
  errorMessage?: string;
  failedAction?: string;
}

export function generateDebugExecutionMessages(args: DebugExecutionArgs): PromptMessage[] {
  const systemMessage = `You are a Power Automate debugging expert. Your role is to help users diagnose and fix failed flow runs.

## Debugging Process
1. **Gather Information**
   - Use get_flow to retrieve the flow definition
   - Use get_runs to find the failed run details
   - Identify the failing action and its inputs

2. **Analyze the Error**
   - Parse the error code and message
   - Check the action type and its specific error patterns
   - Look at the action's inputs and expressions

3. **Identify Root Cause**
   - Expression errors (syntax, missing data, type mismatches)
   - Connection issues (expired, insufficient permissions)
   - Data issues (null values, schema changes, size limits)
   - Throttling/rate limits
   - Timeout issues
   - Service availability

4. **Suggest Fixes**
   - Provide specific code/expression fixes
   - Explain why the fix works
   - Suggest preventive measures

## Common Error Patterns

### Expression Errors
- \`InvalidTemplate\`: Syntax error in expression - check quotes, brackets, function names
- \`ExpressionEvaluationFailed\`: Runtime evaluation failed - check for null values
- \`ActionInputsNotValid\`: Invalid input format - verify schema compliance

### Connection Errors
- \`ConnectionNotConfigured\`: Connection reference missing
- \`ConnectionAuthorizationFailed\`: Token expired or permissions revoked
- \`ConnectionProviderError\`: Connector service issue

### HTTP/API Errors
- \`BadRequest\` (400): Invalid request format
- \`Unauthorized\` (401): Authentication failed
- \`Forbidden\` (403): Permission denied
- \`NotFound\` (404): Resource doesn't exist
- \`TooManyRequests\` (429): Rate limited
- \`InternalServerError\` (500): Service error - retry may help

### SharePoint Errors
- \`ItemNotFound\`: Item deleted or ID incorrect
- \`AccessDenied\`: User lacks permissions
- \`ListItemVersionConflict\`: Concurrent modification

### Dataverse Errors
- \`ObjectDoesNotExist\`: Record not found
- \`DuplicateRecord\`: Unique constraint violation
- \`InvalidArgument\`: Schema validation failed

## Expression Debugging Tips
- Use \`coalesce(value, default)\` to handle nulls
- Use \`if(condition, then, else)\` for conditional values
- Check array access with \`length()\` before indexing
- Use \`string()\` to convert types for comparison
- Test expressions in Compose actions first

When debugging:
1. First identify WHAT failed (action name, type)
2. Then identify WHY (error code, message)
3. Then identify WHERE the bad data came from
4. Finally provide a FIX with explanation`;

  const userPrompt = buildDebugPrompt(args);

  return [
    {
      role: "user",
      content: {
        type: "text",
        text: systemMessage,
      },
    },
    {
      role: "user",
      content: {
        type: "text",
        text: userPrompt,
      },
    },
  ];
}

function buildDebugPrompt(args: DebugExecutionArgs): string {
  const parts: string[] = [
    `## Debug Request`,
    ``,
    `**Flow ID:** ${args.flowId}`,
  ];

  if (args.runId) {
    parts.push(`**Run ID:** ${args.runId}`);
  }

  if (args.failedAction) {
    parts.push(`**Failed Action:** ${args.failedAction}`);
  }

  if (args.errorMessage) {
    parts.push(``);
    parts.push(`**Error Message:**`);
    parts.push(`\`\`\``);
    parts.push(args.errorMessage);
    parts.push(`\`\`\``);
  }

  parts.push(``);
  parts.push(`Please help me debug this flow failure:`);
  parts.push(`1. Use get_flow to retrieve the flow definition`);
  parts.push(`2. Use get_runs to find the failed run (${args.runId ?? "most recent"})`);
  parts.push(`3. Identify the root cause of the failure`);
  parts.push(`4. Provide specific fixes I can apply`);
  parts.push(`5. Suggest preventive measures to avoid this in the future`);

  return parts.join("\n");
}

// ============================================================================
// Common Error Quick Fixes
// ============================================================================

export interface ErrorQuickFix {
  errorPattern: RegExp;
  errorType: string;
  description: string;
  fix: string;
  prevention: string;
}

export const errorQuickFixes: ErrorQuickFix[] = [
  {
    errorPattern: /InvalidTemplate.*The template language expression.*cannot be evaluated/i,
    errorType: "Expression Syntax Error",
    description: "The expression has invalid syntax",
    fix: "Check for missing quotes, unbalanced brackets, or incorrect function names. Validate expression in a Compose action first.",
    prevention: "Use expression builder in the portal to validate syntax. Test with sample data.",
  },
  {
    errorPattern: /property.*cannot be found|is null or not an object/i,
    errorType: "Null Reference Error",
    description: "Trying to access a property on a null value",
    fix: "Wrap the expression with coalesce() or add a condition to check for null before accessing.",
    prevention: "Always use coalesce() for optional properties: coalesce(body('Action')?['property'], 'default')",
  },
  {
    errorPattern: /ActionFailed.*status code: 429/i,
    errorType: "Rate Limiting",
    description: "API rate limit exceeded",
    fix: "Add delays between actions, reduce concurrency in loops, or implement exponential backoff.",
    prevention: "Configure retry policy with exponential backoff. Use pagination instead of fetching all at once.",
  },
  {
    errorPattern: /ConnectionNotConfigured/i,
    errorType: "Missing Connection",
    description: "The connection reference is not configured",
    fix: "Edit the flow in the portal and configure the connection reference.",
    prevention: "Ensure connections are set up before deploying flows. Document required connections.",
  },
  {
    errorPattern: /The specified list item was not found/i,
    errorType: "SharePoint Item Not Found",
    description: "The SharePoint item doesn't exist or was deleted",
    fix: "Add a condition to check if the item exists before operating on it.",
    prevention: "Use try/catch pattern with scope actions. Add 'Get item' before 'Update item'.",
  },
];

/**
 * Find a quick fix for a given error message
 */
export function findQuickFix(errorMessage: string): ErrorQuickFix | null {
  for (const fix of errorQuickFixes) {
    if (fix.errorPattern.test(errorMessage)) {
      return fix;
    }
  }
  return null;
}

/**
 * Get all known error patterns
 */
export function getErrorPatterns(): ErrorQuickFix[] {
  return errorQuickFixes;
}
